import { useEffect, useState } from 'react';
import * as api from '@/sync/api';
import { usePos } from '@/store/pos';

/**
 * The shop's bank QR, fetched once per revision.
 *
 * The image is deliberately not in the state blob — see `ShopSettings.qr_image`
 * on the server — so this watches `shop.qrRev` and fetches only when it moves.
 * The cache is module-level rather than in the store because every screen that
 * prints a bill wants the same bytes, and the store holds what the server says
 * the books are, not attachments.
 *
 * A failure is swallowed to `null`: a bill printed without its QR is what the
 * shop had yesterday, and it is not worth a banner in the middle of a sale.
 */
let cachedRev = -1;
let cached: string | null = null;

export function useShopQr(): string | null {
  const rev = usePos((s) => s.shop.qrRev ?? 0);
  const [image, setImage] = useState<string | null>(rev === cachedRev ? cached : null);

  useEffect(() => {
    if (rev === 0) {
      cachedRev = 0;
      cached = null;
      setImage(null);
      return;
    }
    if (rev === cachedRev) {
      setImage(cached);
      return;
    }

    let live = true;
    void api
      .fetchShopQr()
      .catch(() => null)
      .then((found) => {
        cachedRev = rev;
        cached = found;
        if (live) setImage(found);
      });
    return () => {
      live = false;
    };
  }, [rev]);

  return image;
}
